import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { getFirstNonEmpty } from "../utils/common";
import { markForSync } from "./syncService";
import { getAllInvoices } from "./invoiceService";
import formaDePagoService from "./formaDePagoService";
import dayjs from "dayjs";

const resolveInvoiceForCierreLine = (line, invoicesInRange) => {
	if (line.invoiceId != null && line.invoiceId !== "") {
		const byId = invoicesInRange.find((inv) => inv.id === line.invoiceId);
		if (byId) return byId;
	}
	const ref = line.invoiceNo;
	if (ref != null && ref !== "" && ref !== "Desconocido") {
		const byRef = invoicesInRange.find(
			(inv) =>
				inv.No === ref ||
				inv.Pre_Assigned_No === ref ||
				String(inv.id) === String(ref)
		);
		if (byRef) return byRef;
	}
	return undefined;
};

// Funciones para gestionar pagos de facturas

export const addInvoicePaymentLine = async (paymentLine) => {
	try {
		// El Dto_Amount viene ya calculado desde el formulario
		const discountPercentage = paymentLine.Dto || 0;
		const amount = paymentLine.Amount || 0;
		const discountAmount = paymentLine.Dto_Amount || 0;

		// Crear una línea de pago en la base de datos local
		const id = await db.invoicePaymentLines.add({
			invoiceId: paymentLine.invoiceId,
			Document_Type: paymentLine.Document_Type || "Invoice",
			Document_No_: paymentLine.Document_No_,
			Line_No: paymentLine.Line_No,
			Payment_Method: paymentLine.Payment_Method,
			Amount: amount,
			Dto: discountPercentage,
			Dto_Amount: parseFloat(discountAmount.toFixed(2)),
			pendingSync: 1,
			lastModified: new Date(),
			deleted: false,
		});

		return {
			...paymentLine,
			id,
			Dto_Amount: parseFloat(discountAmount.toFixed(2)),
		};
	} catch (error) {
		console.error("Error al crear línea de pago:", error);
		throw error;
	}
};

export const getInvoicePaymentLines = async (invoice) => {
	try {
		if (!invoice) {
			console.warn("No se proporcionó un documento válido");
			return [];
		}

		let result = [];
		let foundByDocumentNo = false;

		// 1. Priorizar búsqueda por número de documento cuando exista
		const documentNoToUse = getFirstNonEmpty(
			invoice.Pre_Assigned_No,
			invoice.No
		);

		if (documentNoToUse) {
			const byDocNo = await db.invoicePaymentLines
				.where("Document_No_")
				.equals(documentNoToUse)
				.and((line) => !line.deleted)
				.toArray();

			foundByDocumentNo = byDocNo.length > 0;
			result = result.concat(byDocNo);
		}

		// 2. Fallback por ID local (Dexie):
		if (invoice.id && (!documentNoToUse || !foundByDocumentNo)) {
			const byId = await db.invoicePaymentLines
				.where("invoiceId")
				.equals(invoice.id)
				.and((line) => !line.deleted)
				.toArray();

			result = result.concat(byId);
		}

		return Object.values(
			result.reduce((acc, line) => {
				const key =
					line.id ??
					`${line.invoiceId ?? ""}-${line.Document_No_ ?? ""}-${line.Line_No ?? ""}-${line.Payment_Method ?? ""}-${line.Amount ?? ""}`;
				acc[key] = line;
				return acc;
			}, {})
		);

	} catch (error) {
		console.error(
			`Error al obtener líneas de pago para factura ${invoice?.No || invoice?.Pre_Assigned_No || invoice.id}:`,
			error
		);
		throw error;
	}
};

export const updateInvoicePaymentLine = async (id, updatedLine) => {
	try {
		// Obtener la línea actual
		const existingLine = await db.invoicePaymentLines.get(id);
		if (!existingLine) {
			throw new Error("La línea de pago no existe");
		}

		const now = getCurrentDateTimeISO();

		// El Dto_Amount viene ya calculado desde el formulario
		const discountAmount = updatedLine.Dto_Amount || 0;

		const lineToUpdate = {
			...updatedLine,
			Dto_Amount: parseFloat(discountAmount.toFixed(2)),
			pendingSync: 1,
			lastModified: now,
		};

		await db.invoicePaymentLines.update(id, lineToUpdate);

		// Marcar como pendiente de sincronización
		await markForSync("invoicePaymentLines", id);

		return { ...lineToUpdate, id };
	} catch (error) {
		console.error(`Error al actualizar línea de pago con ID ${id}:`, error);
		throw error;
	}
};

export const deleteInvoicePaymentLine = async (id) => {
	try {
		// Verificar si la línea existe antes de intentar borrar
		const existingLine = await db.invoicePaymentLines.get(id);

		if (!existingLine) {
			console.warn(`La línea de pago con ID ${id} no existe o ya fue eliminada.`);
			return false;
		}

		await db.invoicePaymentLines.delete(id);

		console.log(`Línea de pago ${id} eliminada físicamente de IndexedDB`);
		return true;
	} catch (error) {
		console.error(`Error al realizar borrado físico de la línea ${id}:`, error);
		throw error;
	}
};

// Obtener todas las líneas de pago de facturas por fecha específica
export const getPaymentLinesByDate = async (date) => {
	try {
		// Formatear la fecha para comparación
		const targetDate = dayjs(date).format("YYYY-MM-DD");

		// Obtener todas las facturas
		const invoices = await getAllInvoices();

		// Filtrar facturas por fecha - INCLUIR TANTO FACTURAS COMO NOTAS DE CRÉDITO
		const invoicesOnDate = invoices.filter((invoice) => {
			const invoiceDate = dayjs(invoice.Posting_Date).format("YYYY-MM-DD");
			const isValidType = ["Invoice", "Credit Memo"].includes(invoice.Document_Type);
			const isNotDraft = !invoice.isDraft;
			return invoiceDate === targetDate && isValidType && isNotDraft;
		});

		if (invoicesOnDate.length === 0) {
			return [];
		}

		// Obtener los IDs de las facturas
		const invoiceIds = invoicesOnDate.map((invoice) => invoice.id);

		// Obtener todas las líneas de pago que pertenezcan a esas facturas
		const allPaymentLines = await db.invoicePaymentLines
			.filter((line) => !line.deleted && invoiceIds.includes(line.invoiceId))
			.toArray();

		// Obtener todas las formas de pago para enriquecer con el tipo de pago
		const formasDePago = await formaDePagoService.getAll();

		// Enriquecer las líneas de pago
		const enrichedPaymentLines = allPaymentLines.map((line) => {
			const relatedInvoice = invoicesOnDate.find(inv => inv.id === line.invoiceId);

			const paymentMethod =
				(line.Payment_Method && line.Payment_Method.trim()) ||
				(relatedInvoice?.Payment_Method && relatedInvoice.Payment_Method.trim()) ||
				"EFECTIVO";

			const formaPago = formasDePago.find(
				(fp) =>
					fp.Code.trim().toUpperCase() === paymentMethod.trim().toUpperCase()
			);

			const rawAmount = line.Amount || 0;
			const adjustedAmount =
				relatedInvoice?.Document_Type === "Credit Memo"
					? -Math.abs(rawAmount)
					: rawAmount;

			return {
				...line,
				rawAmount,
				Amount: adjustedAmount,
				invoiceNo: relatedInvoice?.No || relatedInvoice?.id || "Desconocido",
				clientName: relatedInvoice?.Bill_to_Name || "Cliente desconocido",
				documentType: relatedInvoice?.Document_Type || "Unknown",
				Payment_Method_Code: paymentMethod,
				Tipo_Pago: formaPago?.Tipo_Pago || "Efectivo",
			};
		});

		// AGRUPAR POR FACTURA
		const groupedByInvoice = Object.values(
			enrichedPaymentLines.reduce((acc, line) => {
				const key = `${line.invoiceNo}-${line.Payment_Method_Code}`;
				if (!acc[key]) {
					acc[key] = {
						invoiceNo: line.invoiceNo,
						clientName: line.clientName,
						documentType: line.documentType,
						Payment_Method: line.Payment_Method_Code,
						Tipo_Pago: line.Tipo_Pago,
						Amount: line.Amount,
						lines: [line],
					};
				} else {
					acc[key].Amount += line.Amount;
					acc[key].lines.push(line);
				}
				return acc;
			}, {})
		);
		return groupedByInvoice;
	} catch (error) {
		console.error(`Error al obtener líneas de pago para la fecha ${date}:`, error);
		throw error;
	}
};

// Obtener todas las líneas de pago de facturas en un rango de fecha y hora específico
// (desde apertura de caja hasta momento del cierre)
export const getPaymentLinesByDateTimeRange = async (
	aperturaDateTime,
	cierreDateTime = null,
	historicos = false
) => {
	try {
		const endDateTime = cierreDateTime || new Date();
		const startDateTime = dayjs(aperturaDateTime);
		const endDateTimeFormatted = dayjs(endDateTime);

		console.log(
			`🔍 Buscando facturas desde: ${startDateTime.format(
				"YYYY-MM-DD HH:mm:ss"
			)} hasta: ${endDateTimeFormatted.format("YYYY-MM-DD HH:mm:ss")}`
		);

		// Obtener todas las facturas
		const invoices = await getAllInvoices();

		// Filtrar facturas por rango de fecha y hora usando lastModified
		const invoicesInRange = invoices.filter((invoice) => {
			if (!invoice.lastModified) return false;

			const invoiceDateTime = dayjs(invoice.lastModified);

			const isInRange =
				invoiceDateTime.isSame(startDateTime) ||
				invoiceDateTime.isSame(endDateTimeFormatted) ||
				(invoiceDateTime.isAfter(startDateTime) &&
					invoiceDateTime.isBefore(endDateTimeFormatted));

			const isValidType =
				invoice.Document_Type === "Invoice" ||
				invoice.Document_Type === "Credit Memo";

			const isNotDraft = !invoice.isDraft;

			return isInRange && isValidType && isNotDraft;
		});

		console.log(`📊 Facturas encontradas en el rango: ${invoicesInRange.length}`);

		if (invoicesInRange.length === 0) {
			return [];
		}

		// Obtener los IDs de las facturas
		const invoiceIds = !historicos
			? invoicesInRange.map(invoice => invoice.id)
			: [...new Set(
				invoicesInRange.flatMap(invoice => [
					invoice.No,
					invoice.Pre_Assigned_No
				]).filter(Boolean)
			)];

		let allPaymentLines = [];

		if (historicos) {
			for (const no of invoiceIds) {
				// Buscar líneas de pago para esa factura
				const paymentLines = await db.invoicePaymentLines
					.where('Document_No_')
					.equals(no)
					.toArray();

				// Chequeo si existe en paymentLines, ya que divide por metodo de pago. En invoiceLines solo toma uno
				let hasPaymentLines = paymentLines.length > 0;
				let linesToUse = [];

				if (hasPaymentLines) {
					linesToUse = paymentLines;
				} else {
					// Factura sin líneas de pago → sumar monto total de invoiceLines
					const lines = await db.invoiceLines.filter((l) => l.invoiceNo === no).toArray();
					const totalFactura = lines.reduce((sum, l) => sum + (l.Line_Amount || 0), 0);

					linesToUse = [
						{
							invoiceNo: no,
							rawAmount: totalFactura
						},
					];
				}

				// Mapear y agregar al arreglo principal
				const mappedLines = linesToUse.map((line) => ({
					...line,
					rawAmount: hasPaymentLines ? line.Amount : line.Line_Amount || 0,
					invoiceNo: no,
					Payment_Method:
						(line?.Payment_Method && line?.Payment_Method.trim()) ||
						"EFECTIVO",
				}));

				allPaymentLines.push(...mappedLines);
			}
		} else {
			// Modo local: buscar por invoiceId
			allPaymentLines = await db.invoicePaymentLines
				.filter((line) => !line.deleted && invoiceIds.includes(line.invoiceId))
				.toArray();

			allPaymentLines = allPaymentLines.map((line) => {
				const relatedInvoice = invoicesInRange.find((inv) => inv.id === line.invoiceId);
				return {
					...line,
					rawAmount: line.Amount || 0,
					invoiceNo: relatedInvoice?.No || line.invoiceId,
					Payment_Method: line.Payment_Method || relatedInvoice?.Payment_Method || "EFECTIVO",
				};
			});
		}

		console.log(`💳 Líneas de pago encontradas: ${allPaymentLines.length}`);

		const formasDePago = await formaDePagoService.getAll();

		// Enriquecer y ajustar montos
		const enrichedPaymentLines = allPaymentLines.map((line) => {
			const relatedInvoice = resolveInvoiceForCierreLine(line, invoicesInRange);

			const formaPago = formasDePago.find(
				(fp) =>
					fp.Code.trim().toUpperCase() ===
					(line.Payment_Method || "").trim().toUpperCase()
			);

			// Ajuste de signo si es nota de crédito
			const adjustedAmount =
				relatedInvoice?.Document_Type === "Credit Memo"
					? -Math.abs(line.rawAmount)
					: line.rawAmount;

			const displayDocNo =
				relatedInvoice?.No ||
				line.invoiceNo ||
				relatedInvoice?.Pre_Assigned_No ||
				relatedInvoice?.id;

			return {
				...line,
				Amount: adjustedAmount,
				invoiceNo: displayDocNo != null && displayDocNo !== "" ? displayDocNo : "Desconocido",
				clientName: relatedInvoice?.Bill_to_Name || "Cliente desconocido",
				documentType: relatedInvoice?.Document_Type || "Unknown",
				Payment_Method_Code: line.Payment_Method || "-",
				Tipo_Pago: formaPago?.Tipo_Pago || "Efectivo",
			};
		});

		// Agrupar por factura y acumular montos
		const groupedByInvoice = Object.values(
			enrichedPaymentLines.reduce((acc, line) => {
				const key = `${line.invoiceNo}-${line.Payment_Method_Code}`;
				if (!acc[key]) {
					acc[key] = {
						invoiceNo: line.invoiceNo,
						clientName: line.clientName,
						documentType: line.documentType,
						Payment_Method: line.Payment_Method_Code,
						Tipo_Pago: line.Tipo_Pago,
						Amount: line.Amount, // iniciamos con el monto de la primera línea
						lines: [line],               // y guardamos la primera línea
					};
				} else {
					// si ya existe, vamos acumulando
					acc[key].Amount += line.Amount;
					acc[key].lines.push(line);
				}
				return acc;
			}, {})
		);

		// Total general (solo para logging)
		const totalAmount = groupedByInvoice.reduce(
			(sum, inv) => sum + inv.Amount,
			0
		);
		console.log(`💰 Total de pagos en el rango: $${totalAmount.toFixed(2)}`);
		return groupedByInvoice;
	} catch (error) {
		console.error(
			`Error al obtener líneas de pago para el rango de fechas:`,
			error
		);
		throw error;
	}
};


export const getPaymentLinesByNo = async (nos) => {

	const paymentLines = await db.invoicePaymentLines
		.where('Document_No_')
		.anyOf(nos)
		.toArray();

	return paymentLines;
}
